import type { Spell, Item, ClassFeature, Feat } from '../types';

// Keys for localStorage
const STORAGE_KEYS = {
  spells: 'mythic-card-forge:custom-spells',
  items: 'mythic-card-forge:custom-items',
  classes: 'mythic-card-forge:custom-classes',
  feats: 'mythic-card-forge:custom-feats',
} as const;

/**
 * Ensures every entity has a unique id.
 * Re-keys colliding ids with -2, -3, ... suffixes and returns a new array.
 * This repairs data imported before id generation guaranteed uniqueness.
 */
function ensureUniqueIds<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  let changed = false;

  const healed = items.map((item) => {
    let id = item.id;
    let suffix = 2;
    while (seen.has(id)) {
      id = `${item.id}-${suffix}`;
      suffix++;
    }
    seen.add(id);
    if (id !== item.id) changed = true;
    return id === item.id ? item : { ...item, id };
  });

  return changed ? healed : items;
}

/**
 * Loads a stored array from localStorage, healing colliding ids and
 * persisting the healed result back so ids stay stable across reloads.
 */
function loadStored<T extends { id: string }>(
  key: string,
  parse: (raw: string) => T[],
): T[] {
  try {
    const data = localStorage.getItem(key);
    if (!data) return [];
    const items = parse(data);
    const healed = ensureUniqueIds(items);
    if (healed !== items) {
      localStorage.setItem(key, JSON.stringify(healed));
    }
    return healed;
  } catch (error) {
    console.warn(`Failed to load custom data from localStorage (${key}):`, error);
    return [];
  }
}

/**
 * Saves custom spells to localStorage
 */
export function saveCustomSpells(spells: Spell[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.spells, JSON.stringify(spells));
  } catch (error) {
    console.warn('Failed to save custom spells to localStorage:', error);
  }
}

/**
 * Loads custom spells from localStorage
 */
export function loadCustomSpells(): Spell[] {
  return loadStored<Spell>(STORAGE_KEYS.spells, (raw) => JSON.parse(raw) as Spell[]);
}

/**
 * Clears custom spells from localStorage
 */
export function clearCustomSpells(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.spells);
  } catch (error) {
    console.warn('Failed to clear custom spells from localStorage:', error);
  }
}

/**
 * Saves custom items to localStorage
 */
export function saveCustomItems(items: Item[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.items, JSON.stringify(items));
  } catch (error) {
    console.warn('Failed to save custom items to localStorage:', error);
  }
}

/**
 * Loads custom items from localStorage
 */
export function loadCustomItems(): Item[] {
  return loadStored<Item>(STORAGE_KEYS.items, (raw) => JSON.parse(raw) as Item[]);
}

/**
 * Clears custom items from localStorage
 */
export function clearCustomItems(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.items);
  } catch (error) {
    console.warn('Failed to clear custom items from localStorage:', error);
  }
}

/**
 * Saves custom class features to localStorage
 */
export function saveCustomClasses(classes: ClassFeature[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.classes, JSON.stringify(classes));
  } catch (error) {
    console.warn('Failed to save custom classes to localStorage:', error);
  }
}

/**
 * Loads custom class features from localStorage
 */
export function loadCustomClasses(): ClassFeature[] {
  return loadStored<ClassFeature>(STORAGE_KEYS.classes, (raw) => JSON.parse(raw) as ClassFeature[]);
}

/**
 * Clears custom class features from localStorage
 */
export function clearCustomClasses(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.classes);
  } catch (error) {
    console.warn('Failed to clear custom classes from localStorage:', error);
  }
}

/**
 * Saves custom feats to localStorage
 */
export function saveCustomFeats(feats: Feat[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.feats, JSON.stringify(feats));
  } catch (error) {
    console.warn('Failed to save custom feats to localStorage:', error);
  }
}

/**
 * Loads custom feats from localStorage
 */
export function loadCustomFeats(): Feat[] {
  return loadStored<Feat>(STORAGE_KEYS.feats, (raw) => JSON.parse(raw) as Feat[]);
}

/**
 * Clears custom feats from localStorage
 */
export function clearCustomFeats(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.feats);
  } catch (error) {
    console.warn('Failed to clear custom feats from localStorage:', error);
  }
}
